import { useMemo, useState } from "react";
import useReveal from "@/hooks/useReveal";
import useSEO from "@/hooks/useSEO";
import FinalCTA from "@/components/sections/FinalCTA";

const PROJECTS = [
  {
    name: "Vivanta Bhubaneswar",
    category: "Hotels",
    type: "Luxury Hotel · Full Uniform Programme",
    challenge:
      "A complete uniform programme for an established luxury property — every guest-facing department, every shift.",
    direction:
      "Tonal palette aligned to the property interior. Tailored silhouettes with restraint, hidden brand notations.",
    image: "/projects/vivanta-bhubaneswar.jpg",
    fallback:
      "https://images.unsplash.com/photo-1590381105924-c72589b9ef3f?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
  },
  {
    name: "Trident Bhubaneswar",
    category: "Hotels",
    type: "Luxury Hotel · Multi-Department",
    challenge:
      "Hospitality programme calibrated for contemporary guest expectations and operational durability.",
    direction:
      "Refined silhouettes, contrast-piped tunics for F&B, structured patterning for front office.",
    image: "/projects/trident-bhubaneswar.jpg",
    fallback:
      "https://images.unsplash.com/photo-1585418694458-dc80a5c20294?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
  },
  {
    name: "Lyfe Bhubaneswar",
    category: "Hotels",
    type: "Premium Hotel · Uniform Refresh",
    challenge:
      "Refresh of an existing programme — modernise silhouettes while retaining departmental clarity.",
    direction:
      "Updated palette, lighter performance fabrics, role-specific accents.",
    image: "/projects/lyfe-bhubaneswar.jpg",
    fallback:
      "https://images.unsplash.com/photo-1582719508461-905c673771fd?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
  },
  {
    name: "Lemon Tree Bhubaneswar",
    category: "Hotels",
    type: "Hospitality Chain · Property Rollout",
    challenge:
      "Property-level rollout aligned with brand-system guidelines and operational scale.",
    direction:
      "Modular silhouettes, brand-signature accents, durable cycle-tested fabrics.",
    image: "/projects/lemon-tree-bhubaneswar.jpg",
    fallback:
      "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
  },
  {
    name: "The World Backwaters",
    category: "Boutique",
    type: "Boutique Resort · Uniform Programme",
    challenge:
      "Climate-appropriate uniforms for a backwater retreat with discerning guest expectations.",
    direction:
      "Breathable handlooms, leather-tab detailing, tonal contrast stitching.",
    image: "/projects/world-backwaters.jpg",
    fallback:
      "https://images.unsplash.com/photo-1578474846511-04ba529f0b88?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85",
  },
];

const FILTERS = ["All", "Hotels", "Restaurants", "Boutique"];

export default function Projects() {
  useSEO({
    title: "Hospitality Uniform Projects · Auraa Designs Portfolio",
    description:
      "Selected hospitality uniform programmes designed by Auraa Designs — luxury hotels, boutique resorts, premium restaurants across India.",
    path: "/projects",
  });

  const [active, setActive] = useState("All");
  const headRef = useReveal();
  const filtered = useMemo(() => {
    if (active === "All") return PROJECTS;
    return PROJECTS.filter((p) => p.category === active);
  }, [active]);

  return (
    <div data-testid="projects-page">
      {/* Hero */}
      <section className="pt-28 md:pt-36 lg:pt-44 pb-16 md:pb-24">
        <div className="container-luxury">
          <div ref={headRef} className="reveal max-w-4xl">
            <div className="overline mb-6">Portfolio</div>
            <h1 className="font-serif text-[44px] md:text-[60px] lg:text-[80px] leading-[1.02] tracking-tight text-espresso text-balance">
              Selected Hospitality
              <br />
              <em className="italic text-oxblood">Projects.</em>
            </h1>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-espresso-soft font-light leading-relaxed">
              Custom hospitality uniform systems designed for premium
              guest-facing brands — from boutique retreats to multi-property
              rollouts.
            </p>
          </div>

          <div className="mt-14 md:mt-16 flex flex-wrap gap-3 md:gap-2 border-y border-borderwarm py-5">
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setActive(f)}
                data-testid={`filter-${f.toLowerCase()}`}
                className={`px-5 py-2 text-[11px] uppercase tracking-[0.24em] font-medium transition-all duration-500 border ${
                  active === f
                    ? "bg-oxblood text-bone border-oxblood"
                    : "bg-transparent text-espresso border-borderwarm hover:border-oxblood hover:text-oxblood"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-36">
        <div className="container-luxury space-y-20 md:space-y-28">
          {filtered.map((p, i) => (
            <CaseStudy key={p.name} p={p} index={i} />
          ))}

          {filtered.length === 0 && (
            <div className="text-center py-20 text-espresso-soft font-light">
              No projects in this category yet.
            </div>
          )}
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}

function CaseStudy({ p, index }) {
  const ref = useReveal();
  const flipped = index % 2 === 1;
  return (
    <article
      ref={ref}
      data-testid={`case-study-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="reveal grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
    >
      <div className={`lg:col-span-7 ${flipped ? "lg:order-2" : ""}`}>
        <div className="relative aspect-[4/3] overflow-hidden bg-sandstone group">
          <img
            src={p.image}
            alt={p.name}
            loading="lazy"
            onError={(e) => {
              if (e.currentTarget.src !== p.fallback) e.currentTarget.src = p.fallback;
            }}
            className="w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.04]"
          />
          <div className="absolute inset-0 bg-espresso/10 group-hover:bg-espresso/5 transition-colors duration-700" />
        </div>
      </div>

      <div className={`lg:col-span-5 ${flipped ? "lg:order-1" : ""}`}>
        <div className="overline-muted">{p.type}</div>
        <h2 className="mt-3 font-serif text-4xl md:text-5xl lg:text-[54px] leading-[1.05] tracking-tight text-espresso">
          {p.name}
        </h2>
        <dl className="mt-8 space-y-6">
          <DL label="Design Challenge" value={p.challenge} />
          <DL label="Design Direction" value={p.direction} />
        </dl>
        <div className="mt-8 inline-block text-[11px] uppercase tracking-[0.24em] text-oxblood border-b border-oxblood pb-1">
          {p.category}
        </div>
      </div>
    </article>
  );
}

function DL({ label, value }) {
  return (
    <div>
      <dt className="overline-muted mb-2">{label}</dt>
      <dd className="text-[15px] md:text-base leading-relaxed text-espresso-soft font-light">
        {value}
      </dd>
    </div>
  );
}
